import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { transferenciaService, TransferenciaCreate } from '../../services/transferenciaService';
import { productoService } from '../../services/productoService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';

interface TransferenciaFormProps {
  onClose: () => void;
}

const TransferenciaForm = ({ onClose }: TransferenciaFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 200)
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm<TransferenciaCreate>({
    defaultValues: {
      sucursalOrigenId: 0,
      sucursalDestinoId: 0,
      notas: '',
      detalles: [{ productoId: 0, cantidadSolicitada: 0 }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'detalles'
  });

  const sucursalOrigenId = watch('sucursalOrigenId');

  const createMutation = useMutation(transferenciaService.create, {
    onSuccess: () => {
      toast.success('Transferencia creada correctamente');
      queryClient.invalidateQueries(['transferencias']);
      queryClient.invalidateQueries(['existencias']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear transferencia');
    }
  });

  const onSubmit = (data: TransferenciaCreate) => {
    // Filtrar detalles válidos
    const detallesValidos = data.detalles.filter(d => d.productoId > 0 && d.cantidadSolicitada > 0);
    
    if (detallesValidos.length === 0) {
      toast.error('Debe agregar al menos un producto con cantidad válida');
      return;
    }

    createMutation.mutate({
      ...data,
      detalles: detallesValidos
    });
  };

  const productosConInventario = productosData?.data.productos.filter(p => p.manejaInventario) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Nueva Transferencia</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal Origen *
              </label>
              <select
                {...register('sucursalOrigenId', { 
                  required: 'Sucursal origen es requerida',
                  valueAsNumber: true
                })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Selecciona sucursal origen</option>
                {sucursalesData?.data.sucursales.map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>
                    {sucursal.name}
                  </option>
                ))}
              </select>
              {errors.sucursalOrigenId && (
                <p className="text-red-500 text-sm mt-1">{errors.sucursalOrigenId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal Destino *
              </label>
              <select
                {...register('sucursalDestinoId', { 
                  required: 'Sucursal destino es requerida',
                  valueAsNumber: true
                })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Selecciona sucursal destino</option>
                {sucursalesData?.data.sucursales
                  .filter(s => Number(sucursalOrigenId) === 0 || s.id !== Number(sucursalOrigenId))
                  .map((sucursal) => (
                    <option key={sucursal.id} value={sucursal.id}>
                      {sucursal.name}
                    </option>
                  ))}
              </select>
              {errors.sucursalDestinoId && (
                <p className="text-red-500 text-sm mt-1">{errors.sucursalDestinoId.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={2}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Productos *
              </label>
              <button
                type="button"
                onClick={() => append({ productoId: 0, cantidadSolicitada: 0 })}
                className="text-sm text-primary-600 hover:text-primary-900"
              >
                + Agregar Producto
              </button>
            </div>

            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex space-x-2 items-start">
                  <div className="flex-1">
                    <select
                      {...register(`detalles.${index}.productoId`, {
                        required: 'Producto es requerido',
                        valueAsNumber: true
                      })}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={0}>Selecciona producto</option>
                      {productosConInventario.map((producto) => (
                        <option key={producto.id} value={producto.id}>
                          {producto.name} {producto.sku ? `(${producto.sku})` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.detalles?.[index]?.productoId && (
                      <p className="text-red-500 text-xs mt-1">{errors.detalles[index]?.productoId?.message}</p>
                    )}
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.001"
                      min="0.001"
                      placeholder="Cantidad"
                      {...register(`detalles.${index}.cantidadSolicitada`, {
                        required: 'Cantidad es requerida',
                        valueAsNumber: true,
                        min: { value: 0.001, message: 'Mínimo 0.001' }
                      })}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {errors.detalles?.[index]?.cantidadSolicitada && (
                      <p className="text-red-500 text-xs mt-1">{errors.detalles[index]?.cantidadSolicitada?.message}</p>
                    )}
                  </div>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="px-3 py-2 text-red-600 hover:text-red-900"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Creando...' : 'Crear Transferencia'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferenciaForm;

